import React, { useState, useMemo } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inbox as InboxMenuIcon,
  Block as SpamMenuIcon,
  Cancel as NoReplyMenuIcon,
} from "@mui/icons-material";
import { shouldShowReplyAll } from "../helpers/emailUtils";
import { useToast } from "../contexts/ToastContext";

/** Plain text with newlines, bullets, and link URLs preserved (for paste into plain text fields) */
const htmlToStructuredPlainText = (html: string): string => {
  if (!html || typeof html !== "string") return "";
  let t = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<div[^>]*>/gi, "")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/li>/gi, "")
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<h[1-6][^>]*>/gi, "");
  // Preserve link text and URL: "text (url)"
  t = t.replace(/<a\s+[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, (_, url, text) => {
    const inner = text.replace(/<[^>]+>/g, "").trim();
    return inner ? `${inner} (${url})` : url;
  });
  t = t.replace(/<[^>]+>/g, "");
  const div = document.createElement("div");
  div.innerHTML = t;
  return (div.textContent || div.innerText || t).replace(/\n{3,}/g, "\n\n").trim();
};

/** Fallback: strip to plain text only (no structure) */
const stripHtmlToPlainText = (html: string): string => {
  if (!html || typeof html !== "string") return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

export interface ApiMessage {
  id: string;
  content: string;
  message_type: "user" | "system" | "email";
  thread_id: string;
  channel_id: string;
  user_id: string;
  query_id: string | null;
  is_ai_processed?: boolean;
  emailResponse?: any;
  citations?: any[];
  created_at: string;
  updated_at: string;
  /** Attachments on the message (e.g. email attachments), used when forwarding */
  attachments?: Array<{
    attachment_id: string;
    filename: string;
    file_type: string;
    file_size: number;
  }> | null;
}

export interface MessageActionsMenuProps {
  message: ApiMessage;
  channelType?: 'email' | 'message' | 'voice';
  isHovered: boolean;
  isSelectedThreadArchived?: boolean;
  isChannelArchived?: boolean;
  onReply?: (emailResponse: any) => void;
  onReplyAll?: (emailResponse: any) => void;
  onForward?: (
    emailResponse: any,
    attachments?: Array<{
      attachment_id: string;
      filename: string;
      file_type: string;
      file_size: number;
    }> | null
  ) => void;
  onCopy?: (content: string) => void;
  onEditAndSend?: (message: ApiMessage) => void;
  onDeleteDraft?: (message: ApiMessage) => void | Promise<void>;
  isDeletingDraft?: boolean;
  onDelete?: (message: ApiMessage) => void;
  onEditMessage?: (message: any) => void;
  onDeleteMessage?: (message: any) => void;
  messages?: ApiMessage[]; // For finding current message in More menu
  /**
   * Current thread/folder context for classification actions (standard keys: inbox, sent, spam, no_reply).
   * Parent passes the active sidebar category so the More menu matches the list the user is viewing.
   */
  threadClassification?: 'inbox' | 'sent' | 'spam' | 'no_reply' | null;
  /** Move whole thread to target classification (inbox, spam, no_reply) */
  onMoveToClassification?: (classification: 'inbox' | 'spam' | 'no_reply') => void;
  /** Classification currently being moved to (for loading state) */
  movingToClassification?: string | null;
}

const MessageActionsMenu: React.FC<MessageActionsMenuProps> = ({
  message,
  channelType,
  isHovered,
  isSelectedThreadArchived = false,
  isChannelArchived = false,
  onReply,
  onReplyAll,
  onForward,
  onCopy,
  onEditAndSend,
  onDeleteDraft,
  isDeletingDraft = false,
  onDelete,
  onEditMessage,
  onDeleteMessage,
  messages = [],
  threadClassification = null,
  onMoveToClassification,
  movingToClassification = null,
}) => {
  const muiTheme = useMuiTheme();
  const { showSuccess } = useToast();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);
  const [isDeleteDraftConfirmOpen, setIsDeleteDraftConfirmOpen] = useState(false);

  const handleCopy = async () => {
    const content = message.content || "";
    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    const plainText = isHtml ? htmlToStructuredPlainText(content) || stripHtmlToPlainText(content) : content;

    if (onCopy) {
      onCopy(plainText);
      showSuccess("Copied to clipboard");
      return;
    }

    // Put both text/plain (structure preserved) and text/html (styling) so rich paste keeps formatting
    try {
      if (isHtml && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": new Blob([plainText], { type: "text/plain" }),
            "text/html": new Blob([content], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      showSuccess("Copied to clipboard");
    } catch {
      await navigator.clipboard.writeText(plainText);
      showSuccess("Copied to clipboard");
    }
  };

  // Show Reply All only when To has >1 or CC/BCC present – uses shared helper
  const showReplyAll = shouldShowReplyAll(message?.emailResponse);

  const handleMoreMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(e.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleReplyFromMenu = () => {
    handleMoreMenuClose();
    const currentMessage = messages.find((msg) => msg.emailResponse);
    if (currentMessage?.emailResponse && onReply) {
      onReply(currentMessage.emailResponse);
    }
  };

  const handleReplyAllFromMenu = () => {
    handleMoreMenuClose();
    const currentMessage = messages.find((msg) => msg.emailResponse);
    if (currentMessage?.emailResponse && onReplyAll) {
      onReplyAll(currentMessage.emailResponse);
    }
  };

  const handleForwardFromMenu = () => {
    handleMoreMenuClose();
    const currentMessage = messages.find((msg) => msg.emailResponse);
    if (currentMessage?.emailResponse && onForward) {
      onForward(
        currentMessage.emailResponse,
        currentMessage.attachments ?? undefined
      );
    }
  };

  const handleCopyFromMenu = () => {
    handleMoreMenuClose();
    handleCopy();
  };

  const handleDeleteFromMenu = () => {
    handleMoreMenuClose();
    if (onDelete) {
      onDelete(message);
    }
  };

  const handleOpenDeleteDraftConfirm = () => {
    setIsDeleteDraftConfirmOpen(true);
  };

  const handleCancelDeleteDraft = () => {
    if (isDeletingDraft) return;
    setIsDeleteDraftConfirmOpen(false);
  };

  const handleConfirmDeleteDraft = () => {
    if (onDeleteDraft) {
      onDeleteDraft(message);
    }
    setIsDeleteDraftConfirmOpen(false);
  };

  // Move thread to target classification (whole thread; closes menu and calls API via parent)
  const handleMoveToClassification = (classification: 'inbox' | 'spam' | 'no_reply') => {
    handleMoreMenuClose();
    onMoveToClassification?.(classification);
  };

  // Folder-specific thread actions (sidebar category): inbox/sent → spam + no reply; no_reply → inbox + spam; spam → no reply + inbox
  const moveToOptions = useMemo(() => {
    if (!onMoveToClassification || !threadClassification) return [];
    const spam = {
      classification: 'spam' as const,
      label: 'Mark as spam',
      icon: <SpamMenuIcon fontSize="small" />,
    };
    const noReply = {
      classification: 'no_reply' as const,
      label: 'Mark as no reply',
      icon: <NoReplyMenuIcon fontSize="small" />,
    };
    const inbox = {
      classification: 'inbox' as const,
      label: 'Move to inbox',
      icon: <InboxMenuIcon fontSize="small" />,
    };
    if (threadClassification === 'inbox' || threadClassification === 'sent') return [spam, noReply];
    if (threadClassification === 'no_reply') return [inbox, spam];
    if (threadClassification === 'spam') return [noReply, inbox];
    return [];
  }, [threadClassification, onMoveToClassification]);

  return (
    <>
      <Box
        data-testid={`message-actions-${message.id}`}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.5,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
          mt: 0.5,
        }}
      >
        {channelType === 'email' && message.emailResponse ? (
          // Email message actions: Reply, Forward, Copy, More
          <>
            <Tooltip title="Reply">
              {/* Span wrapper so Tooltip works when IconButton is disabled (no pointer events on disabled controls). */}
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  data-testid={`message-reply-${message.id}`}
                  size="small"
                  disabled={isSelectedThreadArchived || isChannelArchived}
                  onClick={() => onReply && onReply(message.emailResponse)}
                  sx={{
                    color: "text.secondary",
                    "&.Mui-disabled": {
                      color: muiTheme.palette.action.disabled,
                    },
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Tooltip>
            {showReplyAll && (
            <Tooltip title="Reply All">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  data-testid={`message-reply-all-${message.id}`}
                  size="small"
                  disabled={isSelectedThreadArchived || isChannelArchived}
                  onClick={() => onReplyAll && onReplyAll(message.emailResponse)}
                  sx={{
                    color: "text.secondary",
                    "&.Mui-disabled": {
                      color: muiTheme.palette.action.disabled,
                    },
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <ReplyAllIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Tooltip>
            )}
            <Tooltip title="Forward">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  data-testid={`message-forward-${message.id}`}
                  size="small"
                  disabled={isSelectedThreadArchived || isChannelArchived}
                  onClick={() =>
                    onForward &&
                    onForward(message.emailResponse, message.attachments ?? undefined)
                  }
                  sx={{
                    color: "text.secondary",
                    "&.Mui-disabled": {
                      color: muiTheme.palette.action.disabled,
                    },
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <ReplyIcon
                    sx={{
                      fontSize: 16,
                      transform: "scaleX(-1)",
                    }}
                  />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title="Copy">
              <IconButton
                data-testid={`message-copy-${message.id}`}
                size="small"
                onClick={handleCopy}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="More">
              <IconButton
                data-testid={`message-more-${message.id}`}
                size="small"
                sx={{ color: "text.secondary" }}
                onClick={handleMoreMenuOpen}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Edit & Send button for draft messages */}
            {message.is_ai_processed && message.message_type === "email" && (
              <>
                <Button
                  data-testid={`message-edit-send-${message.id}`}
                  size="small"
                  variant="text"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  disabled={isDeletingDraft}
                  onClick={() => onEditAndSend && onEditAndSend(message)}
                  sx={{
                    color: "primary.main",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    minWidth: "auto",
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    border: "1px solid rgba(25, 118, 210, 0.2)",
                    borderRadius: "6px",
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "rgba(25, 118, 210, 0.12)",
                      border: "1px solid rgba(25, 118, 210, 0.3)",
                    },
                  }}
                >
                  Edit & Send
                </Button>
              </>
            )}
            {/* Non-email message actions: Copy only */}
            <Tooltip title="Copy">
              <IconButton
                data-testid={`message-copy-internal-${message.id}`}
                size="small"
                onClick={handleCopy}
                sx={{
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {message.is_ai_processed && message.message_type === "email" && (
              <Tooltip title="Delete Draft">
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <IconButton
                    data-testid={`message-delete-draft-${message.id}`}
                    size="small"
                    disabled={isDeletingDraft}
                    onClick={handleOpenDeleteDraftConfirm}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        backgroundColor: muiTheme.palette.action.hover,
                        borderRadius: "50%",
                        color: "error.main",
                      },
                      "&.Mui-disabled": {
                        color: muiTheme.palette.action.disabled,
                      },
                    }}
                  >
                    {isDeletingDraft ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Box>
              </Tooltip>
            )}
            {message.message_type === 'user' && (
              <Tooltip title="Edit">
                <IconButton
                  data-testid={`message-edit-${message.id}`}
                  size="small"
                  onClick={() => onEditMessage && onEditMessage(message)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {message.message_type === 'user' && (
              <Tooltip title="Delete">
                <IconButton
                  data-testid={`message-delete-internal-${message.id}`}
                  size="small"
                  onClick={() => onDeleteMessage && onDeleteMessage(message)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* More Menu Dropdown */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={handleReplyFromMenu}
          disabled={isSelectedThreadArchived || isChannelArchived}
        >
          <ListItemIcon>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        {channelType === 'email' && message?.emailResponse && showReplyAll && (
        <MenuItem
          onClick={handleReplyAllFromMenu}
          disabled={isSelectedThreadArchived || isChannelArchived}
        >
          <ListItemIcon>
            <ReplyAllIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply All</ListItemText>
        </MenuItem>
        )}
        <MenuItem
          onClick={handleForwardFromMenu}
          disabled={isSelectedThreadArchived || isChannelArchived}
        >
          <ListItemIcon>
            <ReplyIcon
              fontSize="small"
              sx={{ transform: "scaleX(-1)" }}
            />
          </ListItemIcon>
          <ListItemText>Forward</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyFromMenu}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        {/* Move thread to another category (inbox / spam / no_reply) – whole thread; use array not Fragment — MUI Menu rejects Fragment children. */}
        {moveToOptions.length > 0 && [
          <Divider key="move-to-before" sx={{ my: 1 }} />,
          ...moveToOptions.map(({ classification, label, icon }) => (
            <MenuItem
              key={classification}
              onClick={() => handleMoveToClassification(classification)}
              disabled={movingToClassification !== null}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText>
                {movingToClassification === classification ? 'Moving...' : label}
              </ListItemText>
            </MenuItem>
          )),
          <Divider key="move-to-after" sx={{ my: 1 }} />,
        ]}
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={isDeleteDraftConfirmOpen}
        onClose={handleCancelDeleteDraft}
        aria-labelledby="delete-draft-dialog-title"
        aria-describedby="delete-draft-dialog-description"
      >
        <DialogTitle id="delete-draft-dialog-title">Delete Message?</DialogTitle>
        <DialogContent>
          <Typography id="delete-draft-dialog-description">
            Are you sure you want to delete this message?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteDraft}
            disabled={isDeletingDraft}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteDraft}
            disabled={isDeletingDraft}
            color="error"
            autoFocus
            sx={{
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.08)",
              },
            }}
          >
            {isDeletingDraft ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessageActionsMenu;
